import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import dayjs from "dayjs";
import Select from "react-select";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Badge } from "reactstrap";
import { toast } from "react-toastify";
import PaymentModal from "./Payment";

const PaymentList = () => {
    const dispatch = useDispatch();

    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    // Filters States
    const [invoiceId, setInvoiceId] = useState("");
    const [method, setMethod] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    // ⭐ PAYMENT MODAL STATES
    const [payModal, setPayModal] = useState(false);
    const [selectedInvoiceForPay, setSelectedInvoiceForPay] = useState(null);

    const limit = 20;

    const methodOptions = [
        { value: "", label: "All Methods" },
        { value: "Cash", label: "Cash" },
        { value: "Bank Transfer", label: "Bank Transfer" },
        { value: "Cheque", label: "Cheque" },
        { value: "PayNow", label: "PayNow" }
    ];

    const loadPayments = async () => {
        dispatch(loadingTrue());
        let url = `/payments/list.php?page=${page}&limit=${limit}`;

        if (invoiceId) url += `&invoice_id=${invoiceId}`;
        if (method?.value) url += `&payment_method=${method.value}`;
        if (fromDate) url += `&date_from=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&date_to=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setData(res?.data || []);
            setTotal(res?.pagination?.total_rows || 0);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => {
        loadPayments();
    }, [page, method]);

    const handleSearch = () => {
        setPage(1);
        loadPayments();
    };

    const resetFilters = () => {
        setInvoiceId("");
        setMethod(null);
        setFromDate(null);
        setToDate(null);
        setPage(1);
        loadPayments();
    };

    // ⭐ ADD PAYMENT HANDLER (UPDATED FOR FIX)
    const handleAddPayment = (row) => {
        console.log("Selected invoice for payment:", row);
        setSelectedInvoiceForPay({
            invoice_id: row.invoice_id,
            invoice_number: row.invoice_number,
            customer_id: row.customer_id,
            invoice_total: parseFloat(row.invoice_total || 0),
            total_paid: parseFloat(row.amount || 0),
            remaining_balance: parseFloat(row.invoice_total || 0) - parseFloat(row.amount || 0)
        });

        setPayModal(true);
    };

    return (
        <div className="page-card p-4 bg-white rounded-4 shadow-sm">
            <div className="page-header d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <h4 className="fw-bold mb-0 text-dark">Payment History</h4>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <input
                            type="text"
                            placeholder="Invoice ID..."
                            className="form-control form-control-sm"
                            style={{ width: 120 }}
                            value={invoiceId}
                            onChange={e => setInvoiceId(e.target.value)}
                        />
                        <div style={{ minWidth: 160 }}>
                            <Select
                                options={methodOptions}
                                value={method}
                                onChange={(val) => setMethod(val)}
                                placeholder="Method"
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                        <div style={{ width: 150 }}>
                            <DatePicker
                                label="From"
                                value={fromDate}
                                onChange={(val) => setFromDate(val)}
                                slotProps={{ textField: { size: "small" } }}
                            />
                        </div>
                        <div style={{ width: 150 }}>
                            <DatePicker
                                label="To"
                                value={toDate}
                                onChange={(val) => setToDate(val)}
                                slotProps={{ textField: { size: "small" } }}
                            />
                        </div>
                        <button className="btn btn-primary btn-sm px-3" onClick={handleSearch}>Search</button>
                        <button className="btn btn-outline-secondary btn-sm px-3" onClick={resetFilters}>Reset</button>
                    </div>
                </LocalizationProvider>
            </div>

            <div className="table-responsive">
                <table className="table modern-table align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>ID</th>
                            <th>Date</th>
                            <th>Customer</th>
                            <th>Invoice #</th>
                            <th>Method</th>
                            <th>Reference</th>
                            <th className="text-end">Amount</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr><td colSpan="9" className="text-center py-5 text-muted">No payments found</td></tr>
                        ) : data?.map((row) => (
                                <tr key={row.payment_id}>
                                    <td className="small text-muted">#{row.payment_id}</td>
                                    <td className="fw-semibold">{dayjs(row.payment_date).format("DD-MM-YYYY")}</td>
                                    <td>{row.company_name}</td>
                                    <td><Badge color="light" className="text-primary border">INV-{row.invoice_id}</Badge></td>
                                    <td>{row.payment_method}</td>
                                    <td className="small">{row.reference_number || "-"}</td>
                                    <td className="text-end fw-bold text-success">SGD {parseFloat(row.amount).toFixed(2)}</td>
                                    <td className="text-center">
                                        <Badge color={row.invoice_status === 'paid' ? 'success' : 'warning'} pill className="text-capitalize">
                                            {row.invoice_status}
                                        </Badge>
                                    </td>
                                    <td className="text-center">
                                        {row.invoice_status !== 'paid' && (
                                            <button
                                                className="btn btn-sm btn-success py-0 px-2"
                                                title="Add Payment"
                                                onClick={() => handleAddPayment(row)}
                                            >
                                                <i className="bi bi-cash-stack me-1"></i> Pay
                                            </button>
                                        )}
                                    </td>
                                </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page}
                total={total}
                limit={limit}
                onPrev={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => p + 1)}
            />

            {selectedInvoiceForPay && (
                <PaymentModal
                    isOpen={payModal}
                    toggle={() => setPayModal(!payModal)}
                    invoiceData={selectedInvoiceForPay}
                    onSuccess={() => {
                        toast.success("Payment recorded!");
                        loadPayments();
                    }}
                />
            )}
        </div>
    );
};

export default PaymentList;